import {
  Button,
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextImage from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from '../contexts/app';
import usePromotion, { Promotion } from '../hooks/usePromotion';

type PromotionPopupProps = {
  /** プロモーション確認が完了した（表示不要 or モーダルを閉じた）際に呼ばれる */
  onResolved?: () => void;
};

export const PromotionPopup: React.FC<PromotionPopupProps> = ({
  onResolved,
}) => {
  const { isAuthenticated } = useAppContext();
  const {
    promotion,
    fetchPromotions,
    dequeue,
    clearQueue,
    recordAction,
    isFetched,
  } = usePromotion();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const resolved = useRef(false);

  const resolve = useCallback(() => {
    if (!resolved.current) {
      resolved.current = true;
      onResolved?.();
    }
  }, [onResolved]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPromotions();
    } else {
      // ログアウト等で未認証になった場合はキューとモーダルをクリアする
      clearQueue();
      setIsOpenModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (promotion) {
      setIsOpenModal(true);
    }
  }, [promotion]);

  // フェッチ完了後、またはキューが空になったら（全プロモーション確認済み）resolve
  useEffect(() => {
    if (isFetched && !promotion) {
      resolve();
    }
  }, [isFetched, promotion, resolve]);

  const handleCloseModal = () => {
    setIsOpenModal(false);
    // キューの次のプロモーションへ進む。キューが空になったら上の useEffect で resolve される
    dequeue();
  };

  const handleConfirmModal = () => {
    setIsOpenModal(false);
    // リンク先に遷移するケースのみ残りのキューをクリアする。
    // linkUrl がない場合は次のプロモーションへ進め、全件確認後は useEffect 側で resolve する。
    if (promotion?.linkUrl) {
      clearQueue();
      return;
    }
    dequeue();
  };

  if (!promotion) {
    return null;
  }

  return (
    <PromotionModal
      key={promotion.id}
      isOpen={isOpenModal}
      onClose={handleCloseModal}
      onConfirm={handleConfirmModal}
      promotion={promotion}
      recordAction={recordAction}
    />
  );
};

type PromotionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  promotion: Promotion;
  recordAction: (promotionId: number, actionType: string) => Promise<void>;
};

const PromotionModal: React.FC<PromotionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  promotion,
  recordAction,
}) => {
  const hasRecordedShow = useRef(false);

  useEffect(() => {
    if (isOpen && !hasRecordedShow.current) {
      hasRecordedShow.current = true;
      recordAction(promotion.id, 'show');
    }
  }, [isOpen, promotion.id, recordAction]);

  const handleDismiss = () => {
    recordAction(promotion.id, 'click_dismiss');
    onClose();
  };

  const handleBackdropClose = () => {
    recordAction(promotion.id, 'click_backdrop');
    onClose();
  };

  const handleConfirm = async () => {
    await recordAction(promotion.id, 'click_confirm');
    onConfirm();
    if (promotion.linkUrl) {
      window.location.href = promotion.linkUrl;
    }
  };

  const handleClose = () => {
    recordAction(promotion.id, 'click_close');
    onClose();
  };

  return (
    <Modal
      size={'md'}
      blockScrollOnMount={false}
      isOpen={isOpen}
      onClose={handleBackdropClose}
    >
      <ModalOverlay />
      <ModalContent borderRadius="lg">
        <ModalHeader>
          <Center>
            <NextImage
              src="/images/logo_corp.png"
              width={215}
              height={79}
              alt="Antaa ロゴ"
            />
          </Center>
        </ModalHeader>

        <ModalCloseButton onClick={e => {
          e.preventDefault();
          handleClose();
        }} />
        <ModalBody mx={['10px', '20px']}>
          <VStack spacing={4}>
            {!!promotion.title && (
              <Text align="center" fontSize={'lg'} fontWeight={'bold'}>
                {promotion.title}
              </Text>
            )}
            <Text align="center" fontSize={'2xl'} fontWeight={'bold'}>
              {promotion.description}
            </Text>
          </VStack>

          {promotion.imageUrl && (
            <Center mt={4} mb={7}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={promotion.imageUrl}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '12px',
                  borderColor: 'lightgray',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  boxShadow: '0 2px 4px lightgray',
                }}
                alt={promotion.title ?? ''}
              />
            </Center>
          )}

          <Center>
            <VStack w="full" spacing={5} mb={10}>
              <Button
                colorScheme="brand"
                fontSize="lg"
                w="full"
                h="auto"
                py={6}
                borderRadius="40px"
                onClick={handleConfirm}
              >
                {promotion.confirmButtonLabel}
              </Button>
              <Button
                variant="outline"
                fontSize="md"
                minW="auto"
                w="60%"
                h="auto"
                py={6}
                borderRadius="40px"
                onClick={handleDismiss}
              >
                {promotion.dismissButtonLabel}
              </Button>
            </VStack>
          </Center>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
